import {
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { AttachmentPreviewGrid } from '@/components/shared/AttachmentPreviewGrid'
import { DeleteDocumentDialog } from '@/components/shared/DeleteDocumentDialog'
import { FileUploadDropzone } from '@/components/shared/FileUploadDropzone'
import {
  ClientTabSection,
  clientTabSearchInputClass,
} from '@/components/clients/tabs/ClientTabSection'
import { useClientDocuments } from '@/hooks/useClients'
import { useListParams } from '@/hooks/useListParams'
import { clientsApi } from '@/services/clients'
import { getApiErrorMessage } from '@/services/api'
import { toast } from '@/components/ui/toast'
import { useQueryClient } from '@tanstack/react-query'
import { DOCUMENT_FILE_ACCEPT, isDocumentFileExtension } from '@/lib/constants'
import { getFileSizeLimitMb, isFileTooLarge } from '@/lib/uploadValidation'
import type { AttachmentRef } from '@/components/shared'
import type { Documentable } from '@/types'
import { cn } from '@/lib/utils'

function decodedFileName(fileName: string | null | undefined) {
  if (!fileName) return ''
  try {
    return decodeURIComponent(fileName)
  } catch {
    return fileName
  }
}

// 2.4.14 Client details — documents tab.
export function DocumentsTab({
  clientId,
  canEditClient = false,
}: {
  clientId: string
  canEditClient?: boolean
}) {
  const { t } = useTranslation(['clients', 'common', 'shared'])
  const qc = useQueryClient()
  const { search, onSearchChange, query } = useListParams()
  const { data, isLoading, isError } = useClientDocuments(clientId, query)
  const inputRef = useRef<HTMLInputElement>(null)
  const [deleteTarget, setDeleteTarget] = useState<Documentable | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isDraggingUpload, setIsDraggingUpload] = useState(false)

  const documents = useMemo(() => data?.data ?? [], [data?.data])
  const attachmentItems = useMemo<AttachmentRef[]>(
    () =>
      documents.map(document => ({
        id: document.id,
        file_id: document.file_id,
        name: decodedFileName(document.file_name),
        url: document.url,
        mime_type: document.file_type,
        size: document.file_size,
        created_at: document.created_at,
        doc_type: document.doc_type,
        source: 'upload',
      })),
    [documents]
  )
  const refresh = () =>
    qc.invalidateQueries({ queryKey: ['clients', clientId, 'documents'] })

  async function uploadFiles(files: FileList | File[]) {
    if (files.length === 0 || isUploading) return

    let uploaded = 0
    setIsUploading(true)
    try {
      for (const file of Array.from(files)) {
        const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
        if (!isDocumentFileExtension(extension)) {
          toast({
            variant: 'destructive',
            title: t('documents.unsupportedFormat'),
          })
          continue
        }
        if (isFileTooLarge(file)) {
          toast({
            variant: 'destructive',
            title: t('shared:attachFiles.tooLarge', {
              max: getFileSizeLimitMb(),
            }),
          })
          continue
        }

        try {
          await clientsApi.uploadDocument(clientId, file)
          uploaded += 1
        } catch (error) {
          toast({
            variant: 'destructive',
            title: getApiErrorMessage(error, t('documents.uploadError')),
          })
        }
      }

      if (uploaded > 0) {
        toast({ variant: 'success', title: t('documents.uploadSuccess') })
        await refresh()
      }
    } finally {
      setIsUploading(false)
    }
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''
    if (files.length > 0) void uploadFiles(files)
  }

  function handleUploadDrag(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault()
    event.stopPropagation()
    if (isUploading) return
    setIsDraggingUpload(event.type === 'dragenter' || event.type === 'dragover')
  }

  function handleUploadDrop(event: DragEvent<HTMLButtonElement>) {
    event.preventDefault()
    event.stopPropagation()
    setIsDraggingUpload(false)
    const files = event.dataTransfer.files
    if (files.length > 0 && !isUploading) void uploadFiles(files)
  }

  async function confirmDelete() {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      await clientsApi.deleteDocument(clientId, deleteTarget.id)
      toast({ variant: 'success', title: t('documents.deleteSuccess') })
      await refresh()
      setDeleteTarget(null)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(error, t('documents.deleteError')),
      })
    } finally {
      setIsDeleting(false)
    }
  }

  async function renameDocument(item: AttachmentRef, name: string) {
    if (!item.id || !canEditClient) return
    await clientsApi.renameDocument(clientId, item.id, name)
    toast({ variant: 'success', title: t('documents.renameSuccess') })
    await refresh()
  }

  return (
    <ClientTabSection
      title={t('detail.tabs.documents')}
      headerAction={
        <div className='relative w-[260px]'>
          <Search className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#3d434e]' />
          <Input
            value={search}
            onChange={event => onSearchChange(event.target.value)}
            placeholder={t('documents.searchPlaceholder')}
            aria-label={t('documents.searchPlaceholder')}
            maxLength={50}
            className={cn(clientTabSearchInputClass, 'max-w-none')}
          />
        </div>
      }
    >
      {canEditClient ? (
        <div className='flex flex-col gap-1'>
          <div className='text-xs leading-4 text-[#171a20]'>
            {t('documents.uploadLabel')}{' '}
            <span className='text-[#d63b33]'>*</span>
          </div>
          <input
            ref={inputRef}
            type='file'
            multiple
            className='hidden'
            accept={DOCUMENT_FILE_ACCEPT}
            disabled={isUploading}
            onChange={handleFileChange}
          />
          <FileUploadDropzone
            className='min-h-[64px] rounded-xl'
            onClick={() => inputRef.current?.click()}
            onDragEnter={handleUploadDrag}
            onDragOver={handleUploadDrag}
            onDragLeave={handleUploadDrag}
            onDrop={handleUploadDrop}
            disabled={isUploading}
            loading={isUploading}
            loadingText={t('documents.uploading')}
            dragActive={isDraggingUpload}
            primaryText={t('documents.dropzoneTitle')}
            secondaryText={t('documents.dropzoneHint', {
              max: getFileSizeLimitMb(),
            })}
          />
        </div>
      ) : null}

      {isLoading ? (
        <div className='flex flex-wrap items-start gap-4 pt-4'>
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className='w-[160px] shrink-0 overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_rgba(38,45,66,0.1),0_1px_4px_rgba(38,45,66,0.05)]'
            >
              <Skeleton className='h-[120px] w-full rounded-none' />
              <div className='space-y-2 px-4 py-3'>
                <Skeleton className='h-5 w-24' />
                <Skeleton className='h-4 w-20' />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className='rounded-2xl border border-dashed border-[#e7e9ef] bg-[#fbfbfc] px-6 py-10 text-center text-destructive'>
          {t('documents.error')}
        </div>
      ) : documents.length === 0 ? (
        <div className='rounded-2xl border border-dashed border-[#e7e9ef] bg-[#fbfbfc] px-6 py-10 text-center text-[#6c7285]'>
          {t('documents.empty')}
        </div>
      ) : (
        <AttachmentPreviewGrid
          items={attachmentItems}
          removeLabel={t('documents.actions.delete')}
          onRename={canEditClient ? renameDocument : undefined}
          onRemove={
            canEditClient
              ? item => {
                  const target = documents.find(
                    document => document.id === item.id
                  )
                  if (target) setDeleteTarget(target)
                }
              : undefined
          }
          onItemClick={item => {
            if (item.url) {
              window.open(item.url, '_blank', 'noopener,noreferrer')
            }
          }}
          itemClickLabel={t('documents.actions.download')}
        />
      )}

      <DeleteDocumentDialog
        document={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        isPending={isDeleting}
      />
    </ClientTabSection>
  )
}
